import { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';

const API = 'https://affinityconcept.tech/api';

// Parse a "YYYY-MM-DD" string as a local date (avoids UTC tz shifting the day).
function parseDate(str) {
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function formatDate(str) {
  return parseDate(str).toLocaleDateString('en-AE', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });
}

function money(value) {
  if (value === null || value === undefined) return '—';
  return `$${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// 8-10 green, 5-7 orange, 1-4 red
function scoreColor(score) {
  const s = Number(score);
  if (s >= 8) return 'bg-green-50 text-green-500 border-green-200';
  if (s >= 5) return 'bg-orange-50 text-orange-500 border-orange-200';
  return 'bg-red-50 text-red-500 border-red-200';
}

export default function Analysis({ token, onLogout }) {
  const [user, setUser] = useState(null);
  const [byDate, setByDate] = useState({});   // { 'YYYY-MM-DD': [picks] }
  const [dates, setDates] = useState([]);     // distinct dates, newest first
  const [index, setIndex] = useState(0);      // 0 = newest available date
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/me`, { headers }).then(r => setUser(r.data)).catch(console.error);

    Promise.all([
      axios.get(`${API}/analysis/today`, { headers }).then(r => r.data).catch(() => []),
      axios.get(`${API}/analysis/history?days=90`, { headers }).then(r => r.data).catch(() => []),
    ]).then(([today, history]) => {
      const groups = {};
      // History first, then today, so today's rows are guaranteed present.
      [...history, ...today].forEach(p => {
        if (!p || !p.date) return;
        const key = p.date.slice(0, 10);
        if (!groups[key]) groups[key] = [];
        if (!groups[key].some(x => x.id === p.id)) groups[key].push(p);
      });
      Object.values(groups).forEach(arr =>
        arr.sort((a, b) => Number(b.score) - Number(a.score))
      );
      const sortedDates = Object.keys(groups).sort((a, b) => b.localeCompare(a));
      setByDate(groups);
      setDates(sortedDates);
      setIndex(0);
      setLoading(false);
    });
  }, [token]);

  // Left = older (forward in the desc array), right = newer.
  const navigate = (dir) => {
    setIndex(prev => {
      const next = prev + dir;
      if (next < 0 || next > dates.length - 1) return prev;
      return next;
    });
  };

  const currentDate = dates[index];
  const picks = currentDate ? (byDate[currentDate] || []) : [];
  const isLatest = index === 0;

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <Sidebar onLogout={onLogout} user={user} />
      <main className="flex-1 overflow-y-auto p-4 md:p-8 pt-16 md:pt-8">

        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Market Analysis 📈</h1>
            <p className="text-gray-500 text-sm">AI-picked stocks from daily technical screening</p>
          </div>
        </div>

        {/* Date Navigator */}
        <div className="flex items-center justify-between bg-white rounded-2xl px-5 py-3 shadow-sm border border-gray-100 mb-6">
          <button onClick={() => navigate(1)} disabled={index >= dates.length - 1}
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-xl transition text-gray-500 font-bold text-xl disabled:opacity-30 disabled:cursor-not-allowed">
            ‹
          </button>
          <div className="text-center">
            <p className="text-sm font-semibold text-gray-900">
              {currentDate ? formatDate(currentDate) : 'No analysis yet'}
            </p>
            <p className="text-xs text-gray-400">
              {currentDate
                ? `${isLatest ? '📍 Latest' : '📊 Historical'} · ${picks.length} pick${picks.length === 1 ? '' : 's'}`
                : '—'}
            </p>
          </div>
          <button onClick={() => navigate(-1)} disabled={index <= 0}
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-xl transition text-gray-500 font-bold text-xl disabled:opacity-30 disabled:cursor-not-allowed">
            ›
          </button>
        </div>

        {/* Picks */}
        {loading ? (
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 text-center text-sm text-gray-400">
            Loading analysis…
          </div>
        ) : picks.length === 0 ? (
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 text-center text-sm text-gray-400">
            No stock picks available for this day.
          </div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {picks.map(pick => (
              <div key={pick.id} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">

                {/* Card header: company + symbol + score */}
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h2 className="text-base font-bold text-gray-900">{pick.company}</h2>
                    <p className="text-xs text-gray-400 font-medium tracking-wide">{pick.symbol}</p>
                  </div>
                  <div className={`flex flex-col items-center justify-center px-3 py-1.5 rounded-xl border ${scoreColor(pick.score)}`}>
                    <span className="text-lg font-bold leading-none">{Number(pick.score).toFixed(1)}</span>
                    <span className="text-[10px] opacity-70">/ 10</span>
                  </div>
                </div>

                {/* Price grid */}
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-[11px] text-gray-500 mb-0.5">Entry</p>
                    <p className="text-sm font-bold text-gray-900">{money(pick.entry_price)}</p>
                  </div>
                  <div className="bg-green-50 rounded-xl p-3">
                    <p className="text-[11px] text-gray-500 mb-0.5">Target</p>
                    <p className="text-sm font-bold text-green-500">{money(pick.target_price)}</p>
                  </div>
                  <div className="bg-red-50 rounded-xl p-3">
                    <p className="text-[11px] text-gray-500 mb-0.5">Stop loss</p>
                    <p className="text-sm font-bold text-red-500">{money(pick.stop_loss)}</p>
                  </div>
                </div>

                {/* Buy / Sell conditions */}
                <div className="space-y-2 mb-4">
                  <div className="flex gap-2">
                    <span className="text-xs font-semibold text-green-500 whitespace-nowrap">🟢 Buy when</span>
                    <span className="text-xs text-gray-600">{pick.buy_when}</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-xs font-semibold text-red-500 whitespace-nowrap">🔴 Sell when</span>
                    <span className="text-xs text-gray-600">{pick.sell_when}</span>
                  </div>
                </div>

                {/* Timeframe */}
                {pick.timeframe && (
                  <div className="mb-3">
                    <span className="inline-block text-[11px] font-medium text-blue-500 bg-blue-50 border border-blue-100 rounded-full px-2.5 py-1">
                      ⏱ {pick.timeframe}
                    </span>
                  </div>
                )}

                {/* Reasoning */}
                <div className="border-t border-gray-50 pt-3">
                  <p className="text-xs text-gray-500 leading-relaxed">{pick.reasoning}</p>
                </div>

              </div>
            ))}
          </div>
        )}

      </main>
    </div>
  );
}
